// @flow
//Core
import React, { useState, useEffect, useMemo } from 'react';
import get from 'lodash.get';
import { graphql, Link } from 'gatsby';
import { useSelector, useDispatch } from 'react-redux';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Box, Button } from '@material-ui/core';

//Local
import { NavigationSec } from 'src/components/mediaCenterSections/pressKit';
import { PreviewSec, Intro, Message, HRSec } from '../../components/Recruit';
import { Layout, TopBanner2, TopBannerText } from '../../components';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import * as MediaCenterActions from '../../redux/mediaCenter/actions';

import banner from '../../images/hero-recruit.png';
import mobileBanner from '../../images/hero-recruit-mobile.png';
import firstSectionTertiaryImage1 from '../../images/firstSectionTertiaryImage1.jpeg';
import firstSectionTertiaryImage2 from '../../images/firstSectionTertiaryImage2.png';
import firstSectionPrimaryImage from '../../images/firstSectionPrimaryImage.jpeg';
import firstSectionSecondaryaryImage from '../../images/firstSectionSecondaryaryImage.jpeg';
import {
  SITE_METADATA,
  RECRUIT_PAGE_STATIC_DATA,
  LANGUAGE,
} from '../../constants';

export const query = graphql`
  {
    contentfulPageMediaCenterRecruitment(
      contentful_id: { ne: "5mCqMKbm5Zmj4OU6uhg9np" }
    ) {
      pageSubtitle
      pageTitle
      firstSectionPrimaryText
      firstSectionSecondaryText
      firstSectionPrimaryImage {
        file {
          url
        }
      }
      firstSectionSecondaryaryImage {
        file {
          url
        }
      }
      firstSectionTertiaryImage1 {
        file {
          url
        }
      }
      firstSectionTertiaryImage2 {
        file {
          url
        }
      }
      secondSectionSubtitle
      secondSectionTitle
      thirdSectionSubtitle
      thirdSectionTitle
      thirdSectionTags {
        title
        contentful_id
      }
    }
    allContentfulComponentArticle(
      filter: {
        blogSection: { contentful_id: { eq: "1w915a0HhAJPBs9eqSjl19" } }
      }
    ) {
      edges {
        node {
          contentful_id
          date
          blogSection {
            urlKey
          }
          previewImage {
            file {
              url
            }
          }
          title
          tags {
            title
            contentful_id
          }
        }
      }
    }
    site {
      siteMetadata {
        ogImage
        title
        twitterImage
        siteUrl
      }
    }
  }
`;

const Recruitment = ({ data, location }) => {
  const hrPage = useSelector((state) => state.mediaCenter.hrPage);
  const hrArticles = useSelector((state) => state.mediaCenter.hrArticles);
  const dispatch = useDispatch();
  const languageFromRoute = get(location, 'state.language', LANGUAGE.JA);
  const [language, setLanguage] = useState(languageFromRoute);
  const isMobile = useMediaQuery('(max-width:768px)');

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
  };

  const navBlocks = useMemo(
    () => [
      {
        id: 'firstSec',
        title: language === LANGUAGE.JA ? 'はじめに' : 'Intro',
      },
      {
        id: 'secondSec',
        title:
          language === LANGUAGE.JA
            ? 'Paidyからのメッセージ'
            : 'Message from Paidy',
      },
      {
        id: 'thirdSec',
        title: language === LANGUAGE.JA ? 'Paidyのカルチャー' : 'Paidy Culture',
      },
      {
        id: 'LifeAtPaidy',
        title: 'Life at Paidy',
      },
      {
        id: 'fourthSec',
        title: RECRUIT_PAGE_STATIC_DATA[language].job.title,
      },
      {
        id: 'fifthSec',
        title: RECRUIT_PAGE_STATIC_DATA[language].hr.title,
      },
      {
        id: '',
        title: language === LANGUAGE.JA ? 'English' : '日本語',
        onClick: () =>
          handleLanguageChange(
            language === LANGUAGE.JA ? LANGUAGE.EN : LANGUAGE.JA
          ),
      },
    ],
    [language]
  );

  useEffect(() => {
    dispatch(MediaCenterActions.getHrPageAsync());
    dispatch(MediaCenterActions.getHrArticlesAsync());
  }, [dispatch]);

  useEffect(() => {
    if (languageFromRoute !== language) {
      setLanguage(languageFromRoute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageFromRoute]);

  return (
    <Layout
      SEOProps={{
        title: SITE_METADATA.recruit.title,
        description: SITE_METADATA.recruit.description,
      }}
      isRecruitPage
    >
      <TopBanner2
        bgImg={banner}
        textAlign="center"
        hasLanguageSelection
        mobileBgImg={mobileBanner}
        language={language}
        onLanguageChange={handleLanguageChange}
      >
        <TopBannerText>
          {RECRUIT_PAGE_STATIC_DATA[language].bannerText}
        </TopBannerText>
      </TopBanner2>
      <Breadcrumb
        customLevels={[
          {
            path: '/',
            title: 'トップ',
          },
          {
            path: '/recruit',
            title: language === LANGUAGE.JA ? '採用情報' : 'Careers',
          },
        ]}
      />
      <NavigationSec blocks={navBlocks} location={location} />
      <Intro language={language} />
      <Message language={language} />
      <PreviewSec
        language={language}
        isMobile={isMobile}
        title={get(data, 'contentfulPageMediaCenterRecruitment.pageTitle')}
        subtitle={get(
          data,
          'contentfulPageMediaCenterRecruitment.pageSubtitle'
        )}
        primaryText={RECRUIT_PAGE_STATIC_DATA[language].culture.primaryText}
        secondaryText={RECRUIT_PAGE_STATIC_DATA[language].culture.secondaryText}
        primaryImage={firstSectionPrimaryImage}
        secondaryImage={firstSectionSecondaryaryImage}
        tetriaryImage1={firstSectionTertiaryImage1}
        tetriaryImage2={firstSectionTertiaryImage2}
      />

      <Box textAlign="center" id="fourthSec" mt={10} mb={20}>
        <Link to="https://paypal.eightfold.ai/careers?query=paidy&start=0&location=Tokyo%2C++Japan&pid=274920759691&sort_by=relevance&filter_include_remote=1&filter_include_relocation=0">
          <Button variant="outlined" color="primary" width={263} height={52}>
            {RECRUIT_PAGE_STATIC_DATA[language].job.button}
          </Button>
        </Link>
      </Box>

      <HRSec language={language} data={{ hrPage, hrArticles }} />
    </Layout>
  );
};

export default Recruitment;
